import { useSocialContext } from '../context/social';
import { usePaymentProfile } from '../context/payment-profile';
import { SocialAccount } from '../types/social';
import { prizesAreLive } from '../config/prizes';

/**
 * "Perfil completo" del usuario = las DOS cosas juntas:
 *   1. Datos de pago cargados (nombre + email + términos; + alias de MP una vez que los
 *      premios están vivos, ver config/prizes) → usePaymentProfile.
 *   2. La cuenta de TikTok otorgó TODOS los permisos que pedimos en el OAuth.
 *
 * Unifica las dos "notificaciones de perfil incompleto" (el warn icon del header
 * y las tarjetas de Ajustes) en un solo concepto.
 */

// Scopes que pide el OAuth de TikTok (ver backend api/routers/tiktok.py → tiktok_start,
// param `scope`). Si el usuario destilda alguno en la pantalla de permisos de TikTok,
// perdemos la capacidad asociada (ej. sin user.info.profile no hay @handle).
export const REQUIRED_TIKTOK_SCOPES = [
  'user.info.basic',
  'user.info.profile',
  'user.info.stats',
  'video.list',
  'video.upload',
  'video.publish',
] as const;

// Etiquetas legibles para mostrar en Ajustes qué permiso falta.
export const SCOPE_LABELS: Record<string, string> = {
  'user.info.basic':   'Perfil básico (nombre y foto)',
  'user.info.profile': 'Usuario @handle y biografía',
  'user.info.stats':   'Cantidad de seguidores',
  'video.list':        'Ver tus videos',
  'video.upload':      'Subir videos',
  'video.publish':     'Publicar videos',
};

/**
 * Scopes que le faltan a una cuenta. Devuelve [] si los tiene todos, o si no
 * conocemos los scopes (scopes === undefined) — en ese caso NO asumimos incompleto
 * para no tirar un warning falso.
 */
export const missingScopes = (acc: SocialAccount): string[] => {
  if (!acc.scopes) return [];
  const granted = new Set(acc.scopes);
  return REQUIRED_TIKTOK_SCOPES.filter(s => !granted.has(s));
};

export interface AccountMissingScopes {
  account: SocialAccount;
  missing: string[];
}

export interface ProfileCompleteness {
  loading: boolean;
  paymentComplete: boolean;
  permissionsComplete: boolean;
  /** true solo si pago Y permisos están completos. */
  complete: boolean;
  /** Cuentas de TikTok a las que les faltan permisos (para la card de Ajustes). */
  accountsMissingScopes: AccountMissingScopes[];
  /** Unión de todos los scopes faltantes (para listarlos una sola vez). */
  missingScopesUnion: string[];
}

export const useProfileCompleteness = (): ProfileCompleteness => {
  const { accounts } = useSocialContext();
  const { complete: backendPaymentComplete, loading, profile } = usePaymentProfile();

  // El backend marca `complete` con nombre + email + términos. El Alias de Mercado Pago es
  // opcional para el popup, pero NECESARIO para cobrar: una vez que los premios están vivos
  // (prizesAreLive), su ausencia deja el perfil de pago incompleto → dispara el warning (⚠️)
  // del header y de Settings. Antes del go-live el alias está "Próximamente", así que no cuenta.
  const aliasOk = !prizesAreLive() || !!((profile?.mercadopago_alias || '').trim());
  const paymentComplete = backendPaymentComplete && aliasOk;

  const accountsMissingScopes: AccountMissingScopes[] = accounts
    .filter(a => a.provider === 'tiktok')
    .map(account => ({ account, missing: missingScopes(account) }))
    .filter(x => x.missing.length > 0);

  const permissionsComplete = accountsMissingScopes.length === 0;
  const missingScopesUnion = Array.from(
    new Set(accountsMissingScopes.flatMap(x => x.missing)),
  );

  return {
    loading,
    paymentComplete,
    permissionsComplete,
    complete: paymentComplete && permissionsComplete,
    accountsMissingScopes,
    missingScopesUnion,
  };
};
